/* ============================================================
   INTERACTIONS (interactions.js)
   ------------------------------------------------------------
   Owns: likes, share, the comment drawer + fan-gate form,
   and the in-feed merch sheet (expand → size → buy).

   All state is in-memory for the prototype (page refresh
   resets it). Production notes are inline at each seam.
   ============================================================ */

const Interactions = (() => {
  /* ---- in-memory state ---- */
  const liked = new Set();                  // itemIds this visitor liked
  const likeDelta = {};                     // itemId -> +0/+1 on top of seed count
  const comments = JSON.parse(JSON.stringify(SEED_COMMENTS || {}));
  let fanUnlocked = false;                  // flips true after Laylo signup
  let fanProfile = null;                    // {name,email,phone,instagram,tiktok}
  let gateReturn = null;                    // where to send the fan after unlock
  let currentCommentItem = null;
  let currentProduct = null;
  let selectedVariant = null;

  /* ---- tiny helpers ---- */
  const $ = sel => document.querySelector(sel);
  function formatCount(n) {
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  }
  function commentCount(itemId) {
    return formatCount((comments[itemId] || []).length);
  }
  function toast(msg) {
    const t = $('#toast');
    t.textContent = msg;
    t.classList.add('toast--show');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => t.classList.remove('toast--show'), 2200);
  }
  function openSheet(id) {
    $(id).classList.add('sheet--open');
    document.body.classList.add('modal-open');
  }
  function closeSheets() {
    document.querySelectorAll('.sheet').forEach(s => s.classList.remove('sheet--open'));
    document.body.classList.remove('modal-open');
  }

  /* =========================================================
     LIKES — open to everyone, no gate.
     PRODUCTION: POST to a lightweight endpoint (Shopify app
     proxy or serverless fn) keyed by item id; store the
     visitor's likes in a signed cookie.
     ========================================================= */
  function toggleLike(item, btn) {
    const on = liked.has(item.id);
    if (on) { liked.delete(item.id); likeDelta[item.id] = 0; }
    else    { liked.add(item.id);    likeDelta[item.id] = 1; }
    btn.classList.toggle('rail__like--on', !on);
    if (!on) {                       // heart burst animation
      btn.classList.remove('rail__like--burst');
      void btn.offsetWidth;
      btn.classList.add('rail__like--burst');
      spawnHearts(btn);              // light-up + floating hearts
    }
    Feed.updateRailCount(item.id, 'like',
      formatCount(item.likes + (likeDelta[item.id] || 0)));
  }

  /* LIKE LIGHT-UP (Benton, 2026-10-12): purely aesthetic by design —
     likes are not recorded anywhere. On tap, the heart pops (burst),
     a warm glow ring flashes outward (likeGlow, CSS), and a few tiny
     hearts float up off the button and fade. Skipped entirely for
     fans with reduced motion set. */
  function spawnHearts(btn) {
    if (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    for (let i = 0; i < 3; i++) {
      const h = document.createElement('span');
      h.className = 'like-heart';
      h.textContent = '❤';
      h.setAttribute('aria-hidden', 'true');
      h.style.setProperty('--dx', (Math.random() * 44 - 22).toFixed(0) + 'px');
      h.style.animationDelay = (i * 90) + 'ms';
      btn.appendChild(h);
      h.addEventListener('animationend', () => h.remove());
    }
  }

  /* =========================================================
     SHARE — Web Share API on mobile, clipboard fallback.
     Clean deep links (2026-10-06): /v/{itemId}. A Shopify URL
     redirect turns the cold load into /?v={itemId}, which
     feed.js resolves — and the address bar is put back to the
     clean /v/ form on arrival. Old ?v= links keep working.
     ========================================================= */
  async function share(item) {
    const url = `${CONFIG.shareBaseUrl.replace(/\/$/, '')}/v/${item.id}`;
    const payload = { title: `${CONFIG.artistName} — ${item.title || 'Watch this'}`, url };
    if (navigator.share) {
      try { await navigator.share(payload); return; } catch (_) { /* cancelled */ }
    }
    copyText(url, 'Link copied — send it to a friend');
  }

  /** Clipboard helper (also used by the presale-code reveal). */
  async function copyText(text, msg) {
    try { await navigator.clipboard.writeText(text); toast(msg || 'Copied'); }
    catch (_) { toast(text); } // last-ditch: at least show it
  }

  /* =========================================================
     COMMENTS — locked behind the fan form.
     PRODUCTION: submit the fan form to Shopify customer
     create (Storefront API customerCreate) or a CRM/Klaviyo
     endpoint; store IG/TikTok in customer metafields.
     Comments themselves need an app-proxy backend.
     ========================================================= */
  function openComments(item) {
    currentCommentItem = item;
    $('#commentsTitle').textContent = `Comments · ${item.title || CONFIG.artistName}`;
    renderComments();
    renderGate();
    openSheet('#commentsSheet');
  }

  function renderComments() {
    const list = $('#commentsList');
    const rows = comments[currentCommentItem.id] || [];
    list.innerHTML = rows.length
      ? rows.map(c => `
          <div class="comment">
            <div class="comment__avatar">${c.author[0].toUpperCase()}</div>
            <div class="comment__body">
              <span class="comment__author">${c.author}</span>
              <span class="comment__text">${c.text}</span>
            </div>
          </div>`).join('')
      : `<div class="comments-empty">No comments yet — start the conversation.</div>`;
  }

  function renderGate() {
    const gate = $('#commentGate');
    const composer = $('#commentComposer');
    gate.style.display = fanUnlocked ? 'none' : '';
    composer.style.display = fanUnlocked ? '' : 'none';
    if (fanUnlocked) $('#commentInput').placeholder =
      `Comment as ${fanProfile?.name?.split(' ')[0] || 'you'}…`;
  }

  /** Single source of truth for the fan wall — feed.js reads this
      for the Backstage Pass tab and bubble lock badges. */
  function isUnlocked() { return fanUnlocked; }

  function submitComment() {
    const input = $('#commentInput');
    const text = input.value.trim();
    if (!text || !fanUnlocked) return;
    const id = currentCommentItem.id;
    (comments[id] = comments[id] || []).push({
      author: fanProfile.instagram?.replace(/^@/, '') || fanProfile.name, text,
    });
    input.value = '';
    renderComments();
    $('#commentsList').scrollTop = $('#commentsList').scrollHeight;
    Feed.updateRailCount(id, 'comment', commentCount(id));
  }

  /* =========================================================
     THE LAYLO WALL
     One signup (Obed's Laylo mailing list) unlocks BOTH
     commenting and the Backstage Pass tab.

     PRODUCTION ARCHITECTURE (API-first — better than the embed):
     Laylo's public API is signup-only: POST the GraphQL mutation
     subscribeToUser(email, phoneNumber) with the account API key
     (server-side — never expose the key in the browser). So the
     clean flow is:
       1. Fan fills OUR in-feed form (this one) → a serverless
          function (Netlify Function / Shopify app proxy) calls
          subscribeToUser → fan lands on the Laylo list, AND we
          record membership in our own tiny store (that record is
          the ground truth Laylo can't give us back).
       2. Function sets a signed httpOnly cookie (~1 year). The
          fan never sees a login again on this device.
       3. NEW DEVICE: fan taps "Already have a pass?", enters
          email → backend checks our membership store → emails a
          6-digit code or magic link to prove ownership → cookie.
          No passwords, ever.
     The prototype simulates 2 & 3 with localStorage + an instant
     unlock (no backend here).
     ========================================================= */

  const STORAGE_KEY = 'op_backstage';   // device-remembered pass (prototype)

  function restorePass() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && saved.unlocked) { fanUnlocked = true; fanProfile = saved.profile || null; }
    } catch (_) { /* storage unavailable — session-only unlock */ }
  }
  function persistPass() {
    try {
      localStorage.setItem(STORAGE_KEY,
        JSON.stringify({ unlocked: true, profile: fanProfile }));
    } catch (_) { /* fine — unlock just won't survive the refresh */ }
  }

  /** @param {string} [source] 'comments' | 'backstage' — where the
      fan came from, so we can send them back there after unlock. */
  function openFanForm(source) {
    gateReturn = source || (currentCommentItem ? 'comments' : null);
    renderLayloWall();
    openSheet('#fanSheet');
  }

  /** ONE SIGNUP, ONE BRAND (Benton, 2026-09-11): the Backstage form
      IS the Laylo signup — no separate "open Laylo" button, no
      second path, nothing that reads like two different signups.
      Laylo's site can't be iframed (X-Frame-Options: SAMEORIGIN)
      and embed.laylo.com only serves per-drop embeds, so the form
      stays ours visually but is presented as — and in production
      posts server-side to — Laylo (subscribeToUser API, see
      architecture note above). The only other path is the small
      "already have a pass" email unlock for returning fans.      */
  function renderLayloWall() { /* single-form flow — nothing to add */ }

  /** Mock signup submit (prototype mode — mirrors the fields the
      real Laylo drop should ask for via custom questions). */
  function submitFanForm(e) {
    e.preventDefault();
    const f = e.target;
    const val = name => f.elements[name].value.trim();
    unlock({
      name: val('name'),
      email: val('email'),
      phone: val('phone'),
      instagram: val('instagram'),
      tiktok: val('tiktok'),
    });
  }

  /** Shared unlock path: flip the wall, refresh chrome, and return
      the fan to wherever they hit the gate. */
  function unlock(profile) {
    fanProfile = profile;
    fanUnlocked = true;
    persistPass();   // device remembered — returning visits stay unlocked
    /* PRODUCTION SEAM — replace persistPass() with the signed
       httpOnly cookie set by the backend (see architecture note). */
    closeSheets();
    renderGate();
    toast(`Backstage pass activated, ${(profile.name || 'friend').split(' ')[0]} 🎟`);
    if (gateReturn === 'comments' && currentCommentItem) {
      Feed.refreshChrome();            // update bubble locks, keep scroll
      openComments(currentCommentItem);
    } else {
      Feed.refresh();                  // re-render (opens Backstage if active)
    }
    gateReturn = null;
  }

  /* =========================================================
     THE GREEN ROOM (members only)
     Two rooms in one sheet:
       GREEN ROOM  — group chat: fans talk to Obed and each other
       DIRECT LINE — private message straight to Obed

     PRODUCTION (handoff Prompt 7 + these non-negotiables):
     • Backend: app-proxy/serverless — GET/POST /lounge (shared,
       paginated) and POST /dm (private → mgmt inbox). Poll every
       ~10s or use SSE; a fan chat doesn't need websockets to feel
       live.
     • Identity: from the Backstage cookie (fan's signup name/IG).
     • MODERATION IS REQUIRED for the group room before launch:
       profanity filter on write, slow mode (e.g. 1 msg / 15s),
       report + block, admin delete for Obed/mgmt, and a pinned
       room-rules message. Ship Direct Line first if moderation
       isn't ready — a private inbox needs none of that.
     Prototype below is in-memory with clearly seeded demo chat.
     ========================================================= */

  // seeded demo messages so the Green Room feels real in review
  /* ROOMS_LIVE (Benton, 2026-09-15): the Green Room + Direct Line
     launch as COMING SOON — one honest message from Obed, disabled
     composer. Flip to true when the Phase 2 backend lands. */
  const ROOMS_LIVE = false;
  const loungeMessages = [
    { author: 'BACKSTAGE', obed: true, text: 'The Green Room opens soon — this is where Backstage members will talk with each other and with Obed. Pass holders get first word.' },
  ];
  let room = 'lounge';   // 'lounge' | 'dm'

  function renderLounge() {
    const t = $('#loungeThread');
    t.innerHTML = loungeMessages.map(m => `
      <div class="lounge-msg${m.obed ? ' lounge-msg--obed' : ''}">
        <span class="lounge-msg__author">${m.author}${m.obed ? ' <span class="meta__verified">✓</span>' : ''}</span>
        <span class="lounge-msg__text">${m.text}</span>
      </div>`).join('');
    t.scrollTop = t.scrollHeight;
  }

  function setRoom(next) {
    room = next;
    $('#loungeTab').classList.toggle('lounge-tab--on', room === 'lounge');
    $('#dmTab').classList.toggle('lounge-tab--on', room === 'dm');
    $('#loungeThread').style.display = room === 'lounge' ? '' : 'none';
    $('#dmThread').style.display = room === 'dm' ? '' : 'none';
    $('#dmInput').placeholder = !ROOMS_LIVE ? 'Coming soon…'
      : room === 'lounge'
      ? 'Say something to the room…' : 'Message Obed privately…';
    $('#dmInput').disabled = !ROOMS_LIVE;
    $('#dmSend').disabled = !ROOMS_LIVE;
  }

  /* THE GREEN ROOM IS LIVE — on Discord (Benton, 2026-10-12).
     One big room where Obed and Backstage members talk:
     #the-green-room (15s slowmode) + #from-obed (Obed-only
     announcements). The invite only surfaces here, inside the
     Laylo-gated Backstage tab, so signup stays the front door.
     The Direct Line remains coming-soon (ROOMS_LIVE handles it)
     until the Phase 2 backend lands. */
  const GREEN_ROOM_INVITE = 'https://discord.gg/HGuCndZbem';

  /** @param {string} [room] 'lounge' (Green Room) | 'dm' (Direct
      Line). Green Room → the Discord server, new tab. Direct
      Line → the in-site sheet (coming soon). */
  function openDm(room) {
    if (room !== 'dm') {
      window.open(GREEN_ROOM_INVITE, '_blank', 'noopener');
      return;
    }
    renderLounge();
    setRoom('dm');
    openSheet('#dmSheet');
  }

  function sendDm() {
    if (!ROOMS_LIVE) { toast('Coming soon 🤍'); return; }
    const input = $('#dmInput');
    const text = input.value.trim();
    if (!text) return;
    if (room === 'lounge') {
      /* PRODUCTION SEAM: POST /lounge {text} (moderated). */
      loungeMessages.push({
        author: fanProfile?.instagram?.replace(/^@/, '') || fanProfile?.name || 'you',
        text,
      });
      renderLounge();
    } else {
      /* PRODUCTION SEAM: POST /dm {text} → mgmt inbox with fan identity. */
      const thread = $('#dmThread');
      const bubble = document.createElement('div');
      bubble.className = 'dm-bubble';
      bubble.textContent = text;
      thread.appendChild(bubble);
      thread.scrollTop = thread.scrollHeight;
      toast('Delivered — Obed reads these on the road 🤍');
    }
    input.value = '';
  }

  /* =========================================================
     MERCH SHEET — tap-to-expand sizing + buy, in-feed.
     ========================================================= */
  function openMerchSheet(product) {
    currentProduct = product;
    selectedVariant = null;
    $('#merchName').textContent = product.name;
    $('#merchPrice').textContent = `$${product.price}`;
    $('#merchDesc').textContent = product.description;
    $('#merchArt').style.setProperty('--accent', product.accent);
    $('#merchArt').innerHTML = productArtSVG(product);   // art or real photo

    const sizes = $('#merchSizes');
    sizes.innerHTML = '';
    product.variants.forEach(v => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'size' + (v.available ? '' : ' size--soldout');
      b.textContent = v.label;
      b.disabled = !v.available;
      b.addEventListener('click', () => {
        selectedVariant = v;
        sizes.querySelectorAll('.size').forEach(x => x.classList.remove('size--on'));
        b.classList.add('size--on');
        $('#merchBuy').disabled = false;
        $('#merchBuy').textContent = `Buy · $${product.price}`;
      });
      sizes.appendChild(b);
    });
    $('#merchBuy').disabled = true;
    $('#merchBuy').textContent = 'Select a size';
    openSheet('#merchSheet');
  }

  function buySelectedVariant() {
    if (!selectedVariant) return;
    closeSheets();
    // Real in-feed cart (cart.js) — opens the drawer so the add is
    // visible. Shopify seams live inside Cart.add()/checkout().
    Cart.add(currentProduct, selectedVariant);
    toast(`${currentProduct.name} (${selectedVariant.label}) added to your bag`);
  }

  /** Returning fan on a new device: email-only unlock.
      PROTOTYPE: unlocks instantly. PRODUCTION: backend checks the
      membership store, emails a 6-digit code / magic link, and only
      then sets the cookie — see the architecture note above. */
  function submitReturnForm(e) {
    e.preventDefault();
    const email = e.target.elements['email'].value.trim();
    if (!email) return;
    unlock({ name: email.split('@')[0], email });
  }

  /* ---- wire static dom (called once from app.js) ---- */
  function init() {
    restorePass();   // returning visitor? pick up their pass first
    // sheet close buttons + backdrop
    document.querySelectorAll('[data-close]').forEach(b =>
      b.addEventListener('click', closeSheets));
    $('#backdrop').addEventListener('click', closeSheets);

    $('#unlockBtn').addEventListener('click', () => openFanForm('comments'));
    $('#fanForm').addEventListener('submit', submitFanForm);
    $('#returnToggle').addEventListener('click', () => {
      const f = $('#returnForm');
      f.style.display = f.style.display === 'none' ? '' : 'none';
    });
    $('#returnForm').addEventListener('submit', submitReturnForm);
    $('#commentSend').addEventListener('click', submitComment);
    $('#commentInput').addEventListener('keydown', e => {
      if (e.key === 'Enter') submitComment();
    });
    $('#merchBuy').addEventListener('click', buySelectedVariant);
    $('#dmSend').addEventListener('click', sendDm);
    $('#dmInput').addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendDm(); }
    });
    // Green Room tab inside the sheet also leads to the Discord room
    $('#loungeTab').addEventListener('click', () =>
      window.open(GREEN_ROOM_INVITE, '_blank', 'noopener'));
    $('#dmTab').addEventListener('click', () => setRoom('dm'));

    window.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeSheets();
    });
  }

  return {
    init, toggleLike, share, openComments, openMerchSheet,
    openFanForm, openDm, copyText, isUnlocked,
    formatCount, commentCount,
  };
})();

